import React from "react";
import { Box, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import avatarImage from "../../assets/coach-test-img.jpg";

interface AudioOnlyAvatarProps {
  isUserSpeaking: boolean;
  isAvatarSpeaking: boolean;
  /** Display name shown below the avatar — e.g. the persona's name */
  name?: string;
  /** Persona image URL; falls back to the default coach image when not provided */
  imageUrl?: string;
}

// ── Keyframes ────────────────────────────────────────────────────────────────

/** Idle: slow subtle pulse — a touch more presence than before */
const idlePulse = keyframes`
  0%, 100% { transform: scale(1);     opacity: 0.70; }
  50%       { transform: scale(1.09); opacity: 0.40; }
`;

/** Speaking — inner ring: clear outward expansion */
const speakInner = keyframes`
  0%   { transform: scale(1);    opacity: 0.65; }
  65%  { transform: scale(1.25); opacity: 0.22; }
  100% { transform: scale(1.32); opacity: 0;   }
`;

/** Speaking — outer ring: lags for layered depth */
const speakOuter = keyframes`
  0%   { transform: scale(1);    opacity: 0.42; }
  65%  { transform: scale(1.44); opacity: 0.12; }
  100% { transform: scale(1.55); opacity: 0;   }
`;

// ── Ring colours — matched to transcript/caption colour coding ───────────────
// Avatar (AI): cyan-blue  — matches rgba(100,200,255) used in LiveCaptions for AI
// User:        warm amber — matches rgba(255,200,100) used in LiveCaptions for user
const RING_COLORS = {
  idle:           "rgba(1, 236, 255, 0.28)",   // subtle brand cyan — neutral idle
  avatarSpeaking: "rgba(100, 200, 255, 0.55)", // cyan-blue — matches AI caption colour
  userSpeaking:   "rgba(150, 80, 255, 0.58)",  // brand purple — matches user speaking
} as const;

// ── Aura ring ─────────────────────────────────────────────────────────────────

interface RingProps {
  isUserSpeaking: boolean;
  isAvatarSpeaking: boolean;
  delayS: number;
  outer?: boolean;
}

const AuraRing: React.FC<RingProps> = ({
  isUserSpeaking,
  isAvatarSpeaking,
  delayS,
  outer = false,
}) => {
  const isSpeaking = isUserSpeaking || isAvatarSpeaking;

  const color = isAvatarSpeaking
    ? RING_COLORS.avatarSpeaking
    : isUserSpeaking
    ? RING_COLORS.userSpeaking
    : RING_COLORS.idle;

  const animation = isSpeaking
    ? outer
      ? `${speakOuter} 1.6s ease-out ${delayS}s infinite`
      : `${speakInner} 1.3s ease-out ${delayS}s infinite`
    : `${idlePulse} 3.6s ease-in-out ${delayS}s infinite`;

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "absolute",
        inset: 0,
        borderRadius: "50%",
        backgroundColor: color,
        animation,
        transition: "background-color 0.7s ease",
        willChange: "transform, opacity",
      }}
    />
  );
};

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Audio-only avatar display.
 * Replaces the LiveKit video feed in audio-only mode.
 * Preserves full layout — only the centre content changes.
 */
const AudioOnlyAvatar: React.FC<AudioOnlyAvatarProps> = ({
  isUserSpeaking,
  isAvatarSpeaking,
  name,
  imageUrl,
}) => {
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "20px",
        // Brand gradient: 25% turquoise → dark navy → 25% purple (matches design reference)
        background:
          "linear-gradient(135deg, rgba(1, 236, 255, 0.20) 0%, #030039 48%, rgba(150, 46, 255, 0.22) 100%)",
      }}
    >
      {/* Aura ring system */}
      <Box
        sx={{
          position: "relative",
          width:  { xs: "160px", sm: "200px", md: "240px", lg: "260px" },
          height: { xs: "160px", sm: "200px", md: "240px", lg: "260px" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {/* Rings: outer → inner */}
        <AuraRing isUserSpeaking={isUserSpeaking} isAvatarSpeaking={isAvatarSpeaking} delayS={0.28} outer />
        <AuraRing isUserSpeaking={isUserSpeaking} isAvatarSpeaking={isAvatarSpeaking} delayS={0.14} />
        <AuraRing isUserSpeaking={isUserSpeaking} isAvatarSpeaking={isAvatarSpeaking} delayS={0} />

        {/* Avatar image */}
        <Box
          component="img"
          src={imageUrl ?? avatarImage}
          alt="Audio-only avatar"
          sx={{
            position: "relative",
            zIndex: 2,
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            objectFit: "cover",
            objectPosition: "center 15%",
            border: isAvatarSpeaking
              ? "2px solid rgba(168, 126, 255, 0.30)"
              : isUserSpeaking
              ? "2px solid rgba(1, 236, 255, 0.30)"
              : "2px solid rgba(255, 255, 255, 0.10)",
            boxShadow: isAvatarSpeaking
              ? "0 0 20px rgba(168, 126, 255, 0.18)"
              : isUserSpeaking
              ? "0 0 20px rgba(1, 236, 255, 0.18)"
              : "0 0 12px rgba(0, 0, 0, 0.50)",
            transition: "box-shadow 0.6s ease, border-color 0.6s ease",
          }}
        />
      </Box>

      {/* Guru name — sits below the avatar circle */}
      {name && (
        <Typography
          sx={{
            color: "rgba(255, 255, 255, 0.80)",
            fontSize: { xs: "0.85rem", md: "0.95rem" },
            fontFamily: "Poppins, Helvetica",
            fontWeight: 500,
            letterSpacing: "0.04em",
            textAlign: "center",
            userSelect: "none",
            // Subtle text shadow to lift name off the gradient
            textShadow: "0 1px 8px rgba(0,0,0,0.5)",
          }}
        >
          {name}
        </Typography>
      )}
    </Box>
  );
};

export default AudioOnlyAvatar;
